import React, { useState, useEffect } from 'react';
import { Trash2, RefreshCw, X } from 'lucide-react';
import { THEME_STORAGE_KEY } from '../utils/theme';

function CacheViewer() {
  const [cacheData, setCacheData] = useState({});
  const [selectedKey, setSelectedKey] = useState(null);
  const [expandedKeys, setExpandedKeys] = useState({});

  useEffect(() => {
    loadCacheData();
  }, []);

  const loadCacheData = () => {
    const allData = {};
    const keys = Object.keys(localStorage);
    
    keys.forEach(key => {
      try {
        const value = localStorage.getItem(key);
        let parsedValue;
        let parseError = null;
        
        try {
          parsedValue = JSON.parse(value);
        } catch (e) {
          parsedValue = value; // Keep as string if not JSON
          parseError = e.message;
        }
        
        // Try to extract metadata if it's a cached object
        let metadata = {};
        if (parsedValue && typeof parsedValue === 'object') {
          if (parsedValue.timestamp) {
            const age = Date.now() - parsedValue.timestamp;
            metadata.age = age;
            metadata.ageFormatted = formatAge(age);
            metadata.timestamp = new Date(parsedValue.timestamp).toISOString();
          }
          if (parsedValue.date) {
            metadata.date = parsedValue.date;
          }
          if (parsedValue.userEmail) {
            metadata.userEmail = parsedValue.userEmail;
          }
          if (parsedValue.activePortfolio) {
            metadata.activePortfolio = parsedValue.activePortfolio;
          }
          if (parsedValue.activeWatchlist) {
            metadata.activeWatchlist = parsedValue.activeWatchlist;
          }
          if (parsedValue.portfolioMap) {
            metadata.portfolioCount = Object.keys(parsedValue.portfolioMap).length;
          }
          if (parsedValue.watchlistMap) {
            metadata.watchlistCount = Object.keys(parsedValue.watchlistMap).length;
          }
          if (Array.isArray(parsedValue.data)) {
            metadata.itemCount = parsedValue.data.length;
          }
          if (parsedValue.data && Array.isArray(parsedValue.data)) {
            metadata.itemCount = parsedValue.data.length;
          }
          if (parsedValue.data && typeof parsedValue.data === 'string' && key === 'latestTradeDateCache') {
            metadata.latestTradeDate = parsedValue.data;
          }
          if (parsedValue.data && typeof parsedValue.data === 'object' && key === 'latestTradeDateByMarketCache') {
            metadata.latestTradeDateUS = parsedValue.data.US;
            metadata.latestTradeDateCA = parsedValue.data.CA;
          }
          if (parsedValue.symbolNameMap) {
            metadata.symbolCount = Object.keys(parsedValue.symbolNameMap).length;
          }
          if (parsedValue.tradeDate) {
            metadata.tradeDate = parsedValue.tradeDate;
          }
          if (parsedValue.activeWatchlist) {
            metadata.activeWatchlist = parsedValue.activeWatchlist;
          }
          if (parsedValue.watchlistStocks && Array.isArray(parsedValue.watchlistStocks)) {
            metadata.watchlistStocksCount = parsedValue.watchlistStocks.length;
          }
          if (parsedValue.watchlistSummary) {
            metadata.hasWatchlistSummary = true;
          }
          if (parsedValue.filters) {
            metadata.hasFilters = true;
            metadata.filterCount = Object.keys(parsedValue.filters).length;
          }
          if (parsedValue.minMaxFilters) {
            metadata.hasMinMaxFilters = true;
            metadata.minMaxFilterCount = Object.keys(parsedValue.minMaxFilters).length;
          }
          if (parsedValue.saved !== undefined) {
            metadata.saved = parsedValue.saved;
            metadata.savedStatus = parsedValue.saved ? 'Saved to DynamoDB' : 'Local only';
          }
          // Extract user email from filterPresets key
          if (key.startsWith('filterPresets_')) {
            const email = key.replace('filterPresets_', '');
            metadata.userEmail = email;
          }
        }
        
        allData[key] = {
          raw: value,
          parsed: parsedValue,
          parseError,
          metadata,
          size: new Blob([value]).size, // Size in bytes
          sizeFormatted: formatBytes(new Blob([value]).size)
        };
      } catch (error) {
        allData[key] = {
          raw: value,
          error: error.message,
          size: new Blob([value]).size,
          sizeFormatted: formatBytes(new Blob([value]).size)
        };
      }
    });
    
    setCacheData(allData);
  };

  const formatAge = (ms) => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${Math.round(ms / 1000)}s`;
    if (ms < 3600000) return `${Math.round(ms / 60000)}m`;
    if (ms < 86400000) return `${Math.round(ms / 3600000)}h`;
    return `${Math.round(ms / 86400000)}d`;
  };

  const formatBytes = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const clearCache = (key) => {
    if (window.confirm(`Are you sure you want to delete cache key "${key}"?`)) {
      localStorage.removeItem(key);
      loadCacheData();
      if (selectedKey === key) {
        setSelectedKey(null);
      }
    }
  };

  const clearAllCache = () => {
    if (window.confirm('Are you sure you want to clear ALL cached data? This cannot be undone.')) {
      const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
      localStorage.clear();
      if (savedTheme) localStorage.setItem(THEME_STORAGE_KEY, savedTheme);
      loadCacheData();
      setSelectedKey(null);
    }
  };

  const toggleExpand = (key) => {
    setExpandedKeys(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const getCacheType = (key) => {
    if (key === 'user') return 'User Authentication';
    if (key === 'portfolioData') return 'Portfolio Data';
    if (key === 'cachedStockData') return 'Stock Data';
    if (key === 'pwStockIndexCache') return 'Portfolio/Watchlist US+CA Index';
    if (key === 'selectedDate') return 'Selected Date';
    if (key.startsWith('newsDataCache_')) return 'News Data';
    if (key.startsWith('portfolioSymbolsCache_')) return 'Portfolio Symbols';
    if (key.startsWith('watchlistSymbolsCache_')) return 'Watchlist Symbols';
    if (key === 'newsPubDates') return 'News Publication Dates';
    if (key === 'newsDate') return 'News Selected Date';
    if (key === 'tradeDatesCache') return 'Trade Dates';
    if (key === 'latestTradeDateCache') return 'Latest Trade Date (US legacy)';
    if (key === 'latestTradeDateByMarketCache') return 'Latest Trade Date (US/CA)';
    if (key === 'symbolNamesCache') return 'Symbol Names';
    if (key === 'liveMarketQuotesCache') return 'Live Market Quotes';
    if (key === 'liveMarketExtendedQuotesCache') return 'Live Market Extended Quotes';
    if (key.startsWith('watchlistData_')) return 'Watchlist Data';
    if (key.startsWith('filterPresets_')) return 'Filter Presets';
    return 'Other';
  };

  const getCacheCategory = (key) => {
    if (key === 'user') return 'auth';
    if (key.includes('portfolio') || key === 'portfolioData') return 'portfolio';
    if (key.includes('watchlist') || key.includes('watchlist')) return 'watchlist';
    if (key.includes('news') || key.includes('News')) return 'news';
    if (key.includes('stock') || key.includes('Stock')) return 'stocks';
    if (key.includes('trade') || key.includes('Trade')) return 'stocks';
    if (key === 'symbolNamesCache') return 'stocks';
    if (key === 'liveMarketQuotesCache') return 'stocks';
    if (key === 'liveMarketExtendedQuotesCache') return 'stocks';
    if (key.startsWith('filterPresets_')) return 'filters';
    return 'other';
  };

  const categories = {
    auth: { name: 'Authentication', keys: [] },
    portfolio: { name: 'Portfolio', keys: [] },
    watchlist: { name: 'Watchlist', keys: [] },
    news: { name: 'News', keys: [] },
    stocks: { name: 'Stocks', keys: [] },
    filters: { name: 'Filter Presets', keys: [] },
    other: { name: 'Other', keys: [] }
  };

  Object.keys(cacheData).forEach(key => {
    const category = getCacheCategory(key);
    categories[category].keys.push(key);
  });

  return (
    <div className="page-content">
      <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2>Client-Side Cache Viewer (Testing)</h2>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button 
              onClick={loadCacheData}
              style={{
                padding: '8px 16px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <RefreshCw size={16} />
              Refresh
            </button>
            <button 
              onClick={clearAllCache}
              style={{
                padding: '8px 16px',
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Trash2 size={16} />
              Clear All
            </button>
          </div>
        </div>

        <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
          <strong>Total Cache Entries:</strong> {Object.keys(cacheData).length} | 
          <strong> Total Size:</strong> {formatBytes(
            Object.values(cacheData).reduce((sum, item) => sum + (item.size || 0), 0)
          )}
        </div>

        {Object.keys(cacheData).length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
            No cached data found in localStorage
          </div>
        ) : (
          Object.entries(categories).map(([categoryKey, category]) => {
            if (category.keys.length === 0) return null;
            
            return (
              <div key={categoryKey} style={{ marginBottom: '30px' }}>
                <h3 style={{ 
                  marginBottom: '15px', 
                  paddingBottom: '10px', 
                  borderBottom: '2px solid #dee2e6',
                  color: '#495057'
                }}>
                  {category.name} ({category.keys.length})
                </h3>
                
                {category.keys.map(key => {
                  const item = cacheData[key];
                  const isExpanded = expandedKeys[key];
                  const cacheType = getCacheType(key);
                  
                  return (
                    <div 
                      key={key}
                      style={{
                        marginBottom: '10px',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px',
                        overflow: 'hidden'
                      }}
                    >
                      <div 
                        style={{
                          padding: '12px 15px',
                          backgroundColor: '#f8f9fa',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          cursor: 'pointer'
                        }}
                        onClick={() => toggleExpand(key)}
                      >
                        <div style={{ flex: 1 }}>
                          <strong style={{ color: '#212529' }}>{key}</strong>
                          <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
                            {cacheType} • {item.sizeFormatted}
                            {item.metadata?.ageFormatted && ` • Age: ${item.metadata.ageFormatted}`}
                            {item.metadata?.itemCount && ` • Items: ${item.metadata.itemCount}`}
                            {item.metadata?.portfolioCount && ` • Portfolios: ${item.metadata.portfolioCount}`}
                            {item.metadata?.watchlistCount && ` • Watchlists: ${item.metadata.watchlistCount}`}
                            {item.metadata?.latestTradeDate && ` • Latest Date: ${item.metadata.latestTradeDate}`}
                            {item.metadata?.latestTradeDateUS && ` • US: ${item.metadata.latestTradeDateUS}`}
                            {item.metadata?.latestTradeDateCA && ` • CA: ${item.metadata.latestTradeDateCA}`}
                            {item.metadata?.symbolCount && ` • Symbols: ${item.metadata.symbolCount}`}
                            {item.metadata?.tradeDate && ` • Trade Date: ${item.metadata.tradeDate}`}
                            {item.metadata?.activeWatchlist && ` • Watchlist: ${item.metadata.activeWatchlist}`}
                            {item.metadata?.watchlistStocksCount && ` • Stocks: ${item.metadata.watchlistStocksCount}`}
                            {item.metadata?.userEmail && ` • User: ${item.metadata.userEmail}`}
                            {item.metadata?.filterCount && ` • Filters: ${item.metadata.filterCount}`}
                            {item.metadata?.minMaxFilterCount && ` • Range Filters: ${item.metadata.minMaxFilterCount}`}
                            {item.metadata?.savedStatus && ` • ${item.metadata.savedStatus}`}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              clearCache(key);
                            }}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: '#dc3545',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            <X size={14} />
                            Delete
                          </button>
                        </div>
                      </div>
                      
                      {isExpanded && (
                        <div style={{ padding: '15px', backgroundColor: 'white' }}>
                          {item.metadata && Object.keys(item.metadata).length > 0 && (
                            <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#e7f3ff', borderRadius: '4px' }}>
                              <strong>Metadata:</strong>
                              <pre style={{ marginTop: '8px', fontSize: '12px', overflow: 'auto' }}>
                                {JSON.stringify(item.metadata, null, 2)}
                              </pre>
                            </div>
                          )}
                          
                          <div>
                            <strong>Data Preview:</strong>
                            <pre style={{ 
                              marginTop: '8px', 
                              padding: '10px', 
                              backgroundColor: '#f8f9fa', 
                              borderRadius: '4px',
                              fontSize: '12px',
                              maxHeight: '400px',
                              overflow: 'auto',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word'
                            }}>
                              {JSON.stringify(item.parsed || item.raw, null, 2)}
                            </pre>
                          </div>
                          
                          {item.parseError && (
                            <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#fff3cd', borderRadius: '4px', color: '#856404' }}>
                              <strong>Parse Error:</strong> {item.parseError}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default CacheViewer;
